import React from 'react'
import PropTypes from 'prop-types'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faShirt} from '@fortawesome/free-solid-svg-icons'
import {translateColor, splitTeams, withoutDate, keepHyphens} from '../match'
import './VersusIntro.css'

// How long the whole intro runs, the css timeline in VersusIntro.css fits in it.
const INTRO_LENGTH = 7500

const FALLBACK_COLORS = ['#c0392b', '#1f4e79']

function BigShirt({shirt})
{
	if (!shirt)
	{
		return null
	}
	const shirtColor = translateColor(shirt.ShirtColor)
	const shortsColor = translateColor(shirt.ShortsColor) ?? shirtColor

	return (
		<span className="fa-stack vi-shirt">
			<FontAwesomeIcon className="fa-stack-1x" icon={faShirt} style={{color: shirtColor}} />
			<FontAwesomeIcon className="fa-stack-1x" icon={faShirt} style={{color: shortsColor, clipPath: 'polygon(100% 0, 100% 100%, 0% 100%)'}} />
		</span>
	)
}

BigShirt.propTypes = {
	shirt: PropTypes.object,
}

// Long team names get a smaller size so they stay within their half.
function nameSize(name)
{
	if (name.length > 26) return '50px'
	if (name.length > 16) return '60px'
	if (name.length > 9) return '72px'
	return '96px'
}

// Light team colours (white, yellow, light blue) get dark text instead of white.
function isLight(color)
{
	const hex = color.replace('#', '')
	const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.substr(i, 2), 16))
	return 0.299 * r + 0.587 * g + 0.114 * b > 170
}

// Full screen "fighting game" versus screen, shown when a match starts.
class VersusIntro extends React.Component
{
	componentDidMount()
	{
		this.timer = setTimeout(() => this.props.onDone(), INTRO_LENGTH)
	}

	// The texts are laid out for the 1920x1080 hall TV and scaled to fit any
	// other screen; the colour halves always fill the whole screen.
	stageScale()
	{
		return Math.min(window.innerWidth / 1920, window.innerHeight / 1080)
	}

	componentWillUnmount()
	{
		clearTimeout(this.timer)
	}

	render()
	{
		const entry = this.props.json
		const teams = splitTeams(entry) ?? [entry.eventName, '']
		const colors = [
			translateColor(entry.leftShirt?.ShirtColor) ?? FALLBACK_COLORS[0],
			translateColor(entry.rightShirt?.ShirtColor) ?? FALLBACK_COLORS[1],
		]
		const sub = withoutDate(entry.subEventName)

		return (
			<div className="vi">
				<div className="vi-shake">
					<div className="vi-half vi-left" style={{'--team': colors[0]}} />
					<div className="vi-half vi-right" style={{'--team': colors[1]}} />
					<div className="vi-split"><div className="vi-split-glow" /><div className="vi-split-line" /></div>

					<div className="vi-stage" style={{transform: 'translate(-50%, -50%) scale(' + this.stageScale() + ')'}}>
						<div className={'vi-team vi-team-left' + (isLight(colors[0]) ? ' vi-light' : '')}>
							<BigShirt shirt={entry.leftShirt} />
							<div className="vi-name" style={{fontSize: nameSize(teams[0])}}>{keepHyphens(teams[0])}</div>
						</div>
						<div className={'vi-team vi-team-right' + (isLight(colors[1]) ? ' vi-light' : '')}>
							<BigShirt shirt={entry.rightShirt} />
							<div className="vi-name" style={{fontSize: nameSize(teams[1])}}>{keepHyphens(teams[1])}</div>
						</div>
	
						<div className="vi-vs">VS</div>
						<div className="vi-top"><span>{entry.timeFrom}{sub ? '  ·  ' + sub : ''}</span></div>
					</div>
				</div>
				<div className="vi-flash" />
			</div>
		)
	}
}

VersusIntro.propTypes = {
	json: PropTypes.object.isRequired,
	onDone: PropTypes.func.isRequired,
}

export default VersusIntro
